import { useState } from 'react';
import type { HomeAnnouncementItem, HomeAnnouncementUpsertParams, MarketConfigDTO } from '../../features/settings/types/settings.types';
import { ConfirmDialog } from '../../components/feedback/ConfirmDialog';
import { formatDateTime } from '../../lib/format';

interface Props {
  items: HomeAnnouncementItem[];
  updatedAt: string | null;
  saving: boolean;
  markets: MarketConfigDTO[];
  onCreate: (params: HomeAnnouncementUpsertParams) => Promise<boolean>;
  onUpdate: (id: string, params: HomeAnnouncementUpsertParams) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
}

export function HomeAnnouncementSection({ items, updatedAt, saving, markets, onCreate, onUpdate, onRemove }: Props) {
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [marketId, setMarketId] = useState('CN');
  const [text, setText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<HomeAnnouncementItem | null>(null);

  const openAdd = () => {
    setEditingId(null);
    setMarketId('CN');
    setText('');
    setFormVisible(true);
  };

  const openEdit = (item: HomeAnnouncementItem) => {
    setEditingId(item.id);
    setMarketId(item.market_id || 'CN');
    setText(item.text);
    setFormVisible(true);
  };

  const cancel = () => {
    setFormVisible(false);
    setEditingId(null);
    setMarketId('CN');
    setText('');
  };

  const handleSubmit = async () => {
    const params: HomeAnnouncementUpsertParams = { market_id: marketId, text: text.trim() };
    const ok = editingId ? await onUpdate(editingId, params) : await onCreate(params);
    if (ok) cancel();
  };

  const handleDelete = async (note: string) => {
    void note;
    if (!deleteTarget) return;
    const ok = await onRemove(deleteTarget.id);
    if (ok) setDeleteTarget(null);
  };

  const canSubmit = text.trim().length > 0 && !saving;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-gray-900">走马灯公告</h2>
        <button
          onClick={openAdd}
          disabled={formVisible}
          className="text-sm text-primary hover:text-primary/80 disabled:opacity-40 transition-colors"
        >
          添加
        </button>
      </div>

      {formVisible && (
        <div className="border border-gray-200 rounded-md p-4 bg-gray-50 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              所属地区 <span className="text-red-500">*</span>
            </label>
            <select
              value={marketId}
              onChange={(e) => setMarketId(e.target.value)}
              className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {markets.map((market) => (
                <option key={market.market_id} value={market.market_id}>
                  {market.market_id} · {market.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              公告文本 <span className="text-red-500">*</span>
            </label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="走马灯公告内容"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving && <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {editingId ? '更新' : '添加'}
            </button>
            <button
              onClick={cancel}
              disabled={saving}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">暂无公告</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">地区</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">公告内容</th>
              <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-3 py-3 text-gray-500">{item.market_id || 'CN'}</td>
                <td className="px-3 py-3 text-gray-900">{item.text}</td>
                <td className="px-3 py-3 text-right space-x-3">
                  <button onClick={() => openEdit(item)} className="text-primary hover:underline">编辑</button>
                  <button onClick={() => setDeleteTarget(item)} className="text-red-500 hover:underline">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {updatedAt && (
        <p className="text-xs text-gray-400">最后更新：{formatDateTime(updatedAt)}</p>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="删除公告"
        description={`确定删除该公告？此操作不可撤销。`}
        confirmLabel="删除"
        variant="danger"
        loading={saving}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
